<template>
  <layout-list-detail :tableTools="toggleTableTools">
    <template slot="header">
      <BaseHeader :content="header" />
    </template>
    <template slot="main">
      <div class="form-wrapper">
        <el-form
          :model="form"
          :rules="rules"
          ref="ruleForm"
        >
          <div class="lg:mb-2" v-if="!creatingNew">
            <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('grouping.groups_created')}}</label>
            <div class="flex justify-between">
              <el-form-item prop="groupingSelected" class="w-full">
                <el-select 
                  class="w-full" 
                  filterable 
                  v-model="form.groupingSelected" 
                  :placeholder="$t('grouping.select_group')"
                  clearable
                  @change="onChangeGroupingSelected()"
                  >
                  <el-option
                    v-for="grouping in groupings"
                    :key="grouping.idAgrupacion"
                    :label="grouping.Nombre"
                    :value="grouping.idAgrupacion">
                  </el-option>
                </el-select>
              </el-form-item>
            </div>
          </div>

          <div class="lg:mb-2" v-if="creatingNew">
            <div class="mandatory">
              <label class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon/>{{$t('app.name')}}</label>
              <el-form-item prop="nombre">
              <el-input
                clearable
                class="w-full"
                v-model="form.nombre"
                maxlength='100'
              />
              </el-form-item>
            </div>
          </div>
        </el-form>
    </div>
    </template>
    <template slot="table-header" v-if="selectedGroup && !creatingNew">
      <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('grouping.sellers_by_group')}}</label>
    </template>
    <template slot="table-core" v-if="selectedGroup && !creatingNew">
      <base-table-pagination
        :total="pagination.total"
        :currentPage="pagination.currentPage"
        :perPage="pagination.pageSize"
        @changeCurrentPage="changeCurrentPage"
        @changePerPage="changePerPage"
      ></base-table-pagination>
      <div class="flex flex-row w-100">
        <base-table-searches @toggleSearchesDialog="toggleSearchesDialog" />
        <base-table-search @toggleFilters="toggleFilters" />
      </div>
    </template>
    <template slot="table-tags" v-if="selectedGroup && !creatingNew">
      <base-table-filter-tags :tags="tags" @removeTag="removeFilterTagHanlder" @removeAllTags="removeAllTagsHandler" @saveTags="saveTagsHandler"/>
    </template>
    <template slot="table-component" v-if="selectedGroup && !creatingNew">
      <kendo-grid-component class="kendo-pointer-default"
        :spanish="spanish"
        :dataResult='kendo.dataResult'
        :kendo="kendo"
        :loading='isGeneralLoading'
        :selectedField="selectedField"
        @datastatechange='dataStateChange'
        @columnreorder='columnReorder'
        @rowclick='openPath'
        @columnssubmit='onColumnsSubmit'
        @sortchange='sortChange'
        @filterchange='filterChange'
        @selectionchange="onSelectionChange"
        @headerselectionchange="onHeaderSelectionChange">
    </kendo-grid-component>
    </template>
    <template slot="layout-extend">
      <base-footer>
        <template slot="actions">
          <base-button-responsive v-if="creatingNew" mode="secondary" :text="$t('app.cancel')" icon="cross" @click="cancelCreating" :loading="isGeneralLoading"/>
          <base-button-responsive v-else mode="secondary" :text="$t('app.back')" icon="cross" @click="goBack" :loading="isGeneralLoading"/>

          <base-button-responsive v-if="creatingNew" mode="primary" :text="$t('app.save')" icon="check" @click="submitForm('ruleForm')" :loading="isGeneralLoading" :disabled="isGeneralLoading"/>

          <base-button-responsive v-if="selectedGroup && !creatingNew" mode='primary' :text="labelBtnExcel" icon='excelxls' @click='exportExcel' :loading='isGeneralLoading || loadingExcel' :disabled="loadingExcel"/>
          <base-button-responsive v-if="selectedGroup && !creatingNew" mode='primary' :text="$t('app.edit_name')" icon='editpencil' @click='toggleEditNameDialog' :loading='isGeneralLoading || loadingExcel' :disabled="isGeneralLoading"/>
          <base-button-responsive v-if="selectedGroup && !creatingNew" mode='primary' :text="$t('grouping.delete')" icon='trash' @click='toggleDeleteDialog' :loading='isGeneralLoading || loadingExcel' :disabled="isGeneralLoading"/>
          <base-button-responsive v-if="selectedGroup && !creatingNew" mode="primary" :text="$t('agenda.link_seller')" icon="link" @click="golinkGrouping" :loading="isGeneralLoading"/>
          <base-button-responsive v-if="!creatingNew" mode="primary" :text="$t('grouping.new_group')" icon="plus" @click="resetViewNew" :loading="isGeneralLoading"/>
        </template>
      </base-footer>
      <base-table-select-actions-expand
        :visibility="hasSelected"
        :selected="rowsSelected"
        :total="rowsTotal"
      >
        <template slot="commons">
          <base-button size="xs" mode="tools" @click="unselect">
            <base-icon name="vall" size="sm" class="mr-1" />
            {{$t('app.unselect')}}
          </base-button>
        </template>
        <template slot="more">
          <base-button-item-list theme="dark" @click="unlink">
            <base-icon name="unlink" class="mr-2" />
              {{$t('app.unlink_entity')}}
          </base-button-item-list>
        </template>
      </base-table-select-actions-expand>
      <Dialog
        :title="$t('grouping.delete')"
        :visible.sync="showModalDelete"
        width="40%"
        custom-class="min-width-dialog"
        center>
        <span class="text-center w-full inline-block dark:text-darkblue-200 word-break-n">{{ processMessage }}</span>
        <template slot="footer" >
          <div class="flex flex-col lg:flex-row w-full lg:w-auto items-center space-y-2 lg:space-y-0 lg:space-x-2">
            <base-button class="w-full" @click="showModalDelete = false" mode="secondary-phantom-blue" :text="$t('app.cancel')"/>
            <base-button class="w-full" mode="secondary-phantom-blue" :text="$t('app.accept')" @click="() => {deleteHandler(); showModalDelete = false}"/>
          </div>
        </template>
      </Dialog>
      <input-text-dialog 
        :dialogOpen="showModalEditName" 
        @toggleDialog="toggleEditNameDialog" 
        @applyDialog="applyEditName"
        :header="$t('grouping.link_to_group')"
        :headerValue="$t('grouping.new_group_name')"
        />
      <link-sellers-dialog 
        :title="$t('agenda.link_sellers')"
        :dialogOpen="linkGroupingSellersDialog" 
        :clientId="this.$route.params.id" 
        @toggleDialog="toggleDialogs" 
        @applyDialog="applyLinkHandler" />
      <filter-panel 
        :panelOpen="filterPanel" 
        @togglePanel="toggleFilters" 
        @applyFilters="applyFiltersHandler" 
        :vuexnamespace="vuexnamespace"
        />
      <create-search-dialog 
        :numberSearches="numberSearches"
        :idVista="idVista" 
        :dialogOpen="saveSearchDialog" 
        :tags="tags" 
        @toggleDialog="toggleDialogSearches" 
        @applyDialog="applySearchHandler" />
      <filter-panel-searches 
        ref="filterPanelSearches" 
        :idVista="idVista" 
        :vuexnamespace="vuexnamespace" 
        :panelOpen="searchesPanel" 
        @togglePanel="toggleSearchesDialog" 
        @applyFilters="applyFiltersHandler" 
        @updateNumberSearches="updateNumberSearches"/>
    </template>
  </layout-list-detail>
</template>

<script>
// import ListLayout from '@/components/layout/ListLayout.vue'
import { i18n } from '@/i18n'
import { Dialog } from 'element-ui'
import { process } from '@progress/kendo-data-query'
import setNestedValue from '@/services/functions/setNestedValue'
import hasNestedValue from '@/services/functions/hasNestedValue'

import LayoutListDetail from '@/components/layout/DetailListLayout.vue'
import FilterPanel from '@/components/filters/SociedadesFilter.vue'
import FilterPanelSearches from '@/components/searches/SearchesPanel.vue'
import CreateSearchDialog from '@/components/searches/CreateSearchDialog.vue'
import KendoGridComponent from '../../components/KendoGridComponent.vue'
import LinkSellersDialog from '@/components/LinkSellersDialog.vue'
import InputTextDialog from '@/components/InputTextDialog.vue'

import { getSociedadesPag } from '@/services/configurations/getSociedadesPag'
import { getSociedadesExcel } from '@/services/configurations/getSociedadesExcel'

import { linkAgrupacionProveedor } from '@/services/configurations/grouping/linkAgrupacionProveedor'
import { unlinkAgrupacionProveedor } from '@/services/configurations/grouping/unlinkAgrupacionProveedor'

import { addAgrupacionProveedor } from '@/services/configurations/grouping/addAgrupacionProveedor'
import { updateAgrupacionProveedor } from '@/services/configurations/grouping/updateAgrupacionProveedor'
import { deleteAgrupacionProveedores } from '@/services/configurations/grouping/deleteAgrupacionProveedores'
import { getAgrupacionesProveedores } from '@/services/configurations/grouping/getAgrupacionesProveedores'

export default {
  /* eslint-disable camelcase */
  name: 'grouping-sellers-view',
  components: {
    // ListLayout,
    LayoutListDetail,
    FilterPanel,
    FilterPanelSearches,
    CreateSearchDialog,
    KendoGridComponent,
    Dialog,
    InputTextDialog,
    LinkSellersDialog,
  },
  data: function () {
    return {
      idVista: 75,
      creatingNew: false,
      // Searches
      numberSearches: 0,
      saveSearchDialog: false,
      searchesPanel: false,
      // All
      showModalDelete: false,
      showModalEditName: false,
      loadingExcel: false,
      showModal: false,
      linkGroupingSellersDialog: false,
      searchView: false,
      activeName: 'es',
      spanish: true,
      header: {
        title: i18n.t('grouping.header_long'),
        icon: 'config',
        breadcrumb: null,
        url: 'configurations'
      },
      vuexnamespace: 'groupingsellers',
      selectedField: 'selected',
      filterPanel: false,
      showAllRows: true,
      loadingDataTable: false,
      data: null,
      groupings: [],
      selected: [],
      form: {
        nombre: '',
        groupingSelected: null,
      },
      rules: {
        nombre: [
          {
            required: true,
            message: i18n.t('app.fill_data'),
            trigger: 'blur'
          }
        ]
      },
      processMessage: i18n.t('app.delete_current_confirmation_f', { entity: i18n.t('grouping.entity_name').toLowerCase() })
    }
  },
  computed: {
    selectedGroup () {
      return this.form.groupingSelected
    },
    labelBtnExcel: function () {
      if (this.hasSelected) return `${i18n.t('app.export_excel')} (${i18n.t('app.selected')})`
      return i18n.t('app.export_excel')
    },
    kendo: function () {
      return this.$store.getters[this.vuexnamespace + '/getKendo']
    },
    role: function () {
      return this.$store.getters['auth/role']
    },
    isHeadCoreReady: function () {
      return this.$store.getters.isHeadCoreReady
    },
    toggleTableTools: function () {
      return this.selected.length > 0
    },
    rowsSelected: function () {
      return this.selected.length
    },
    rowsTotal: function () {
      return this.selected.length
    },
    setLabelTowShow: function () {
      return this.showAllRows ? i18n.t('app.only_selected') : i18n.t('app.see_all')
    },
    masterFilters: function () {
      return this.$store.getters[this.vuexnamespace + '/getMasterFilters']
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    headerCoreFilters: function () {
      return this.$store.getters['auth/queryHeaders']
    },
    tags: function () {
      return this.$store.getters[this.vuexnamespace + '/getTags']
    },
    pagination: function () {
      return this.$store.getters[this.vuexnamespace + '/getPaginationInfo']
    },
    columns () {
      return [
        { field: 'selected', width: '50px', headerSelectionValue: this.areAllSelected, reorderable: false, groupable: false },
        ...this.kendo.columns
      ]
    },
    areAllSelected () {
      return this.data.length === this.selected.length
    },
    hasSelected () {
      return this.selected.length > 0
    },
    isCreate: function () {
      return parseInt(this.$route.params.id) === -1
    },
    clienteId () {
      return this.$route.params.id ? parseInt(this.$route.params.id) : null
    },
    isSuperAdminView () {
      return this.clienteId
    },
    paramClientId () {
      return this.isSuperAdminView ? { idCliente: this.clienteId } : null
    }
  },
  watch: {
    headerCoreFilters: {
      // immediate: true,
      deep: true,
      handler: 'headerCoreFiltersHandler'
    },
    data (val, oldVal) {
      const dataState = {
        skip: this.kendo.skip,
        take: this.kendo.take,
        sort: this.kendo.sort,
        group: this.kendo.group
      }
      const localGroups = dataState.group || []
      const localData = val || []
      if (localData.length && localGroups.length) {
        localData.forEach(x => {
          localGroups.forEach(group => {
            if (!hasNestedValue(group.field, x, { checkNull: true, checkUndefined: true })) {
              setNestedValue(group.field, x, '')
            }
          })
        })
      }
      this.kendo.dataResult = process(localData, dataState)
      this.unselect()
    }
  },
  created: function () {
  },
  mounted: async function () {
    const breadcrumbs = []
    if (this.isSuperAdminView) {
      breadcrumbs.push({
        path: 'configurations/clients/details',
        name: i18n.t('clients.entity_name')
      })
    } else {
      breadcrumbs.push({
        path: 'configurations',
        name: i18n.t('configurations.header')
      })
    }

    await this.downloadData()
    breadcrumbs.push({ 
      path: this.isSuperAdminView ? 'configurations/grouping/sellers' : 'configurations/grouping/sellers/main', 
      name: i18n.tc('grouping.grouping_sellers', 2) 
    })
    this.header.url = this.isSuperAdminView ? 'configurations/clients/details' : 'configurations'
    this.header.breadcrumb = breadcrumbs
    this.$store.commit(this.vuexnamespace + '/setPageSize', this.kendo.take)
    if (this.selectedGroup) await this.applyFilters()
  },
  methods: {
    onChangeGroupingSelected () {
      if (this.selectedGroup) this.applyFilters()
    },
    toggleEditNameDialog () {
      this.showModalEditName = !this.showModalEditName
    },
    resetViewNew () {
      this.creatingNew = true
      // this.form.groupingSelected = null
    },
    cancelCreating () {
      this.creatingNew = false
      this.resetFormVal()
      this.downloadData()
    },
    downloadData: async function (idAgrupToSelect = null) {
      try {
        this.$store.commit('setIsLoading', true)
        const res = await getAgrupacionesProveedores(this.paramClientId)
        if (res.blnError === true) {
          this.doFeedback(`${res.msgError} [${res.codError}]`)
        } else {
          this.groupings = res.Respuesta

          // Si es nuevo se muestra el último creado
          if (idAgrupToSelect === -1) {
            this.form.groupingSelected = res.Respuesta[res.Respuesta.length -1].idAgrupacion
            this.applyFilters()
            this.onChangeGroupingSelected() 
          // Preseleccionamos el primero
          } else if (res.Respuesta.length > 0) {
            this.form.groupingSelected = idAgrupToSelect || res.Respuesta[0].idAgrupacion
            this.onChangeGroupingSelected()
          } else {
            this.form.groupingSelected = null
          }
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    updateAllSelectedHeader: function () {
      if (!this.hasSelected) {
        this.kendo.columns[0].headerSelectionValue = false
      } else {
        this.kendo.columns[0].headerSelectionValue = this.areAllSelected
      }
    },
    watchFormHandler: function ({ certificate, send_alerts }) {
    },
    toggleSearchView: function () {
      this.searchView = !this.searchView
    },
    submitForm: function (formName) {
      this.newGroupingSellersHandler(formName)
    },
    resetForm: function () {
      this.form.nombre = ''
    },
    newGroupingSellersHandler: function (formName) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      this.$refs[formName].validate(async valid => {
        if (valid) {
          const params = {
            Nombre: this.form.nombre,
            idCliente: this.clienteId,
          }
          const response = await addAgrupacionProveedor(params)
          if (response.blnError) {
            this.doFeedback(`${response.msgError} [${response.codError}]`)
          } else {
            this.doFeedback(i18n.t('feedback.success.entity_created_msg'), 'success')
            this.redirectAfterCreating(-1)
          }
        } else {
          this.doFeedback(i18n.t('feedback.error.process_data_error'))
        }
        this.$store.commit('setIsLoading', false)
      })
    },
    redirectAfterCreating (idNewGrouping = null) {
      this.creatingNew = false
      this.resetForm()
      this.resetFormVal()
      this.downloadData(idNewGrouping)
    },
    resetFormVal () {
      this.$refs.ruleForm.resetFields()
    },
    applyEditName: async function (nombre) {
      try {
        this.$store.commit('setIsLoading', true)
        const params = {
          Nombre: nombre,
          idAgrupacion: this.form.groupingSelected,
        }
        const response = await updateAgrupacionProveedor(params)
        if (response.blnError === true) {
          this.doFeedback(`${response.msgError} [${response.codError}]`)
        } else {
          this.doFeedback(i18n.t('feedback.success.entity_updated_msg'), 'success')
          this.toggleEditNameDialog()
          this.downloadData(this.form.groupingSelected)
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    toggleDeleteDialog () {
      this.showModalDelete = !this.showModalDelete
    },
    deleteHandler: async function () {
      try {
        this.$store.commit('setIsLoading', true)
        const params = {
          idAgrupacion: this.form.groupingSelected,
        }
        const response = await deleteAgrupacionProveedores(params)
        if (response.blnError === true) {
          this.doFeedback(`${response.msgError} [${response.codError}]`)
        } else {
          this.doFeedback(i18n.t('feedback.success.entity_deleted_msg'), 'success')
          this.downloadData()
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    goBack: function () {
      if (this.isSuperAdminView) this.$router.push({ name: 'configurations/clients/details' })
      else this.$router.push({ name: 'configurations' })
    },
    openPath: function (event) {
    },
    setWhatShow: function () {
      this.showAllRows = !this.showAllRows
    },
    tableSelected: function (val) {
      this.selected = val
    },
    toggleFilters: function () {
      this.filterPanel = !this.filterPanel
    },
    toggleDialogs: function () {
      this.linkGroupingSellersDialog = !this.linkGroupingSellersDialog
    },
    removeFilterTag: function (tag) {
      this.tags.splice(this.tags.indexOf(tag), 1)
    },
    createAppState: function (dataState) {
      this.kendo.take = dataState.take
      this.kendo.skip = dataState.skip
      if (dataState.group) {
        // eslint-disable-next-line no-return-assign
        // dataState.group.map(group => group.aggregates = this.aggregates)
      }
      this.kendo.group = dataState.group
      this.kendo.filter = dataState.filter
      this.kendo.sort = dataState.sort
    },
    dataStateChange: async function (event) {
      this.createAppState(event.data)
      this.data = await this.applyFilters()
    },
    columnReorder: function (options) {      
      this.sendUpdatedColumns(options.columns)
      this.kendo.columns.forEach((column, index) => {
        if (options.columns.filter(x => x.field === column.field).length === 0) {
          column.orderIndex = index + this.kendo.columns.length
          options.columns.push(column)
        }
      })
      this.kendo.columns = options.columns
    },
    exportExcel: async function () {
      this.$store.commit('app/setLoading', true)
      this.loadingExcel = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      const params = {
        idAgrupacionProveedor: this.selectedGroup,
        // Cliente: data.Cliente ? data.Cliente.value : null,
        NameCorporateName: data.NameCorporateName ? data.NameCorporateName.value : null,
        TIN: data.TIN ? data.TIN.value : null,
        FirstSurname: data.FirstSurname ? data.FirstSurname.value : null,
        SecondSurname: data.SecondSurname ? data.SecondSurname.value : null,
        ResidenceType: data.ResidenceType ? data.ResidenceType.value : null,
        PersonType: data.PersonType ? data.PersonType.value : null,
        Locations: data.Locations ? data.Locations.value : null,
        LinkedCompanies: data.LinkedCompanies ? data.LinkedCompanies.value : null,
        Book: data.Book ? data.Book.value : null,
        RegisterOfCompaniesLocation: data.RegisterOfCompaniesLocation ? data.RegisterOfCompaniesLocation.value : null,
        Sheet: data.Sheet ? data.Sheet.value : null,
        Folio: data.Folio ? data.Folio.value : null,
        Section: data.Section ? data.Section.value : null,
        Volume: data.Volume ? data.Volume.value : null,
        AdditionalData: data.AdditionalData ? data.AdditionalData.value : null,
      }
      const objOrder = this.getObjOrderMixin(this.kendo.group, this.kendo.sort)
      const objColumnas = this.getColumnsGroupExcelMixin(this.kendo.columns, this.kendo.group)
      const payload = { order: objOrder.order, grupos: objColumnas }
      if (this.hasSelected) {
        const selectedIDs = this.selected.map(x => x.IdSociedad)
        payload.listaIDs = selectedIDs
      }
      try {
        const response = await getSociedadesExcel(payload, params)
        const blob = new Blob([response.data], { type: response.data.type, encoding: 'UTF-8' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        let filename = `${(i18n.t('grouping.entity_name')+'-'+i18n.t('agenda.sellers')).toLowerCase()}.xlsx`
        filename = decodeURI(filename)
        link.download = filename
        link.click()
      } catch (error) {
        console.error(error)
        if (error.response?.data) {
          const errorText = await error.response.data.text()
          this.doFeedback(errorText)
        } else {
          this.doFeedback()
        }
      } finally {
        this.$store.commit('app/setLoading', false)
        this.loadingExcel = false
      }
    },
    onColumnsSubmit: function (columnsState) {
      this.sendUpdatedColumns(columnsState)
      this.kendo.columns = columnsState
    },
    sortChange: function (e) {
      let sort = e.sort
      if (Array.isArray(e)) {
        sort = e
      }
      const dataState = {
        skip: this.kendo.skip,
        take: this.kendo.take,
        sort: sort,
        group: this.kendo.group,
        filter: this.kendo.filter
      }
      this.dataStateChange({ data: dataState })
    },
    filterChange: function (filter) {
      const dataState = {
        skip: this.kendo.skip,
        take: this.kendo.take,
        sort: this.kendo.sort,
        group: this.kendo.group,
        filter: filter
      }

      this.dataStateChange({ data: dataState })
    },
    expandChange: function (event) {
      event.dataItem[event.target.$props.expandField] = !event.dataItem[event.target.$props.expandField]
    },
    applyFilters: async function (eventData) {
      this.loadingDataTable = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      const params = {
        idAgrupacionProveedor: this.selectedGroup,
        Cliente: data.Cliente ? data.Cliente.value?.idCliente : null,
        NameCorporateName: data.NameCorporateName ? data.NameCorporateName.value : null,
        TIN: data.TIN ? data.TIN.value : null,
        FirstSurname: data.FirstSurname ? data.FirstSurname.value : null,
        SecondSurname: data.SecondSurname ? data.SecondSurname.value : null,
        ResidenceType: data.ResidenceType ? data.ResidenceType.value : null,
        PersonType: data.PersonType ? data.PersonType.value : null,
        Locations: data.Locations ? data.Locations.value : null,
        LinkedCompanies: data.LinkedCompanies ? data.LinkedCompanies.value : null,
        Book: data.Book ? data.Book.value : null,
        RegisterOfCompaniesLocation: data.RegisterOfCompaniesLocation ? data.RegisterOfCompaniesLocation.value : null,
        Sheet: data.Sheet ? data.Sheet.value : null,
        Folio: data.Folio ? data.Folio.value : null,
        Section: data.Section ? data.Section.value : null,
        Volume: data.Volume ? data.Volume.value : null,
        AdditionalData: data.AdditionalData ? data.AdditionalData.value : null,
      }
      const objOrder = this.getObjOrderMixin(this.kendo.group, this.kendo.sort)
      try {
        this.data = await this.getItemsData(objOrder, params)
      } catch (error) {
        this.$store.commit('setIsLoading', false)
        this.doFeedback()
      }
      this.updateAllSelectedHeader()
      return this.data
    },
    getItemsData: async function (objOrder, filters) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const res = await this.getMainData(objOrder, params)
      this.data = res.respuesta?.map((item) => {
        item.FirstSurname = item.FirstSurname || ''
        item.SecondSurname = item.SecondSurname || ''
        item.RegistrationData = item.RegistrationData || {}
        item.RegistrationData.Book = item.RegistrationData.Book || ''
        item.RegistrationData.RegisterOfCompaniesLocation = item.RegistrationData.RegisterOfCompaniesLocation || ''
        item.RegistrationData.Sheet = item.RegistrationData.Sheet || ''
        item.RegistrationData.Folio = item.RegistrationData.Folio || ''
        item.RegistrationData.Section = item.RegistrationData.Section || ''
        item.RegistrationData.Volume = item.RegistrationData.Volume || ''
        item.RegistrationData.AdditionalData = item.RegistrationData.AdditionalData || ''
        return item
      })

      const pagination = {
        total: res.meta?.total,
        currentPage: res.meta?.currentPage,
        totalPages: res.meta?.lastPage,
        pageSize: res.meta?.perPage
      }
      
      this.$store.commit(this.vuexnamespace + '/setPaginationInfo', pagination)
      this.$store.commit('setIsLoading', false)
      this.loadingDataTable = false
      return this.data
    },
    async getMainData (objOrder, filters) {
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const data = await getSociedadesPag(objOrder, params)
      if (data.blnError === true) {
        this.doFeedback(`${data.msgError} [${data.codError}]`)
      }
      return data
    },
    applyFiltersHandler: async function (eventData) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.data = await this.applyFilters(eventData)
    },
    changeCurrentPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', event)
      this.data = await this.applyFilters(null)
    },
    changePerPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/setPageSize', event)
      this.kendo.take = event
      this.data = await this.applyFilters(null)
    },
    removeFilterTagHanlder: function (tag) {
      // this.$store.commit(this.vuexnamespace + '/removeTag', tag)
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      const filter = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      delete filter[tag[0]]
      this.$store.commit(this.vuexnamespace + '/setMasterFilters', filter)
      this.applyFiltersHandler(null)
    },
    headerCoreFiltersHandler: function (obs) {
      if (this.isHeadCoreReady) {
        this.applyFilters(null)
      }
    },
    removeAllTagsHandler: function () {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/resetMasterFilters')
      this.applyFiltersHandler(null)
    },
    tableSortByChangeHandler: function ({ prop, order }) {
      this.$store.commit(this.vuexnamespace + '/setSort', prop)
      this.$store.commit(this.vuexnamespace + '/setSortOrder', order)
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.applyFilters(null)
    },
    unselect: function () {
      const checked = false
      this.mapSelectionKendoGrouping(checked)
      this.updateAllSelectedHeader()
    },
    unlink: async function () {
      const selected = this.selected
      await this.unlinkHandler(selected)

      this.data = await this.applyFilters()
    },
    unlinkHandler: async function (items) {
      try {
        this.$store.commit('setIsLoading', true)
        const payload = { idAgrupacion: this.selectedGroup, idsSociedad: [] }
        items.forEach(x => {
          payload.idsSociedad.push(x.IdSociedad)
        })
        const response = await unlinkAgrupacionProveedor(payload)
        if (response.blnError === true) {
          this.doFeedback(`${response.msgError} [${response.codError}]`)
        } else {
          this.doFeedback(i18n.t('feedback.success.entity_unlinked_msg'), 'success')
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    golinkGrouping: function () {
      this.linkGroupingSellersDialog = !this.linkGroupingSellersDialog
    },
    onHeaderSelectionChange: function (event) {
      const checked = event.event.target.checked
      this.mapSelectionKendoGrouping(checked)
      this.updateAllSelectedHeader()
    },
    onSelectionChange: function (event) {
      event.dataItem[this.selectedField] = !event.dataItem[this.selectedField]
      this.selected = event.dataItems.filter(d => d.selected === true)
      this.updateAllSelectedHeader()
    },
    applyLinkHandler: async function (items) {
      this.linkGroupingSellers = false
      await this.linkHandler(items)
      this.applyFilters()
    },
    linkHandler: async function (items) {
      try {
        this.$store.commit('setIsLoading', true)
        const payload = { idAgrupacion: this.form.groupingSelected, idsSociedad: items }
        const response = await linkAgrupacionProveedor(payload)
        if (response.blnError === true) {
          this.doFeedback(`${response.msgError} [${response.codError}]`)
        } else {
          this.doFeedback(i18n.t('feedback.success.entity_linked_msg'), 'success')
          this.golinkGrouping()
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    toggleSearchesDialog: function () {
      this.searchesPanel = !this.searchesPanel
    },
    saveTagsHandler: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    toggleDialogSearches: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    applySearchHandler: async function () {
      this.toggleDialogSearches()
      // Manda actualizar lista de búsquedas
      this.$refs.filterPanelSearches.updateData()
    },
    updateNumberSearches: async function (number) {
      this.numberSearches = number
    },
  }
}
</script>
